import React, { memo, useCallback } from 'react';
import { Box, Card, Typography } from '@mui/material';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';
import { getUserSettings, getLocalDate } from '@surfline/web-common';
import { formatTimestampInTimezone, convertToSeconds, oneHourSeconds } from 'utils/time';
import { NearbyStation } from 'types/windStations';
import useActiveWindStation from 'hooks/windStations/useActiveWindStations';
import { MobilePremiumWindStationCTA } from 'components/PremiumWindStationCTA';
import { useMaxWidthTabletLarge } from 'hooks/useMediaQueries';
import Information from 'components/Information';
import { DMY } from '../../common/constants';
import { useAppSelector } from '../../stores/hooks';
import WindStationCardPredominantWindReading from './WindStationCardPredominantWindReading';
import WindStationCardGustsReading from './WindStationCardGustsReading';

import styles from './WindStationCard.module.scss';

interface Props {
  windStation: NearbyStation;
  windSpeedUnit: string;
  isLoading?: boolean;
  showPaywall?: boolean | string;
  isVerticalView?: boolean;
  segmentProperties: {
    pageName: string;
  };
}

const WindStationCard: React.FC<Props> = ({
  windStation,
  windSpeedUnit,
  isLoading = false,
  showPaywall = false,
  isVerticalView = true,
  segmentProperties,
}: Props) => {
  const settings = useAppSelector(getUserSettings);
  const currentTimeInSeconds = Math.floor(
    convertToSeconds(
      getLocalDate(+new Date(), windStation?.lastObserved?.utcOffset || 0).getTime(),
    ) / 1000,
  );

  const isOffline =
    currentTimeInSeconds - (windStation?.lastObserved?.timestamp || 0) > oneHourSeconds;

  const dateFormat =
    settings?.date?.format === DMY ? "h':'mmaaa dd',' MMM yyyy" : "h':'mmaaa MMM dd',' yyyy";
  const [activeWindStationId, setActiveWindStationId] = useActiveWindStation();

  const isSelected = windStation?._id === activeWindStationId;

  const noValue = '--';

  const className = () =>
    classNames({
      [styles.windStationCard]: true,
      [styles.selected]: isSelected,
      [styles.offline]: isOffline,
      [styles.paywall]: showPaywall,
    });

  const handleMakeActive = useCallback(() => {
    setActiveWindStationId(windStation?._id);
  }, [setActiveWindStationId, windStation?._id]);

  const handleDeactivate = useCallback(() => {
    setActiveWindStationId(undefined);
  }, [setActiveWindStationId]);

  const isMobileView = useMaxWidthTabletLarge();

  return (
    <Card
      className={className()}
      data-testid="WindStationCardCard"
      onMouseEnter={handleMakeActive}
      onMouseLeave={handleDeactivate}
      onTouchStart={handleMakeActive}
    >
      <Box className={styles.stationInfo}>
        <Box>
          <Typography variant="subHeadline" className={styles.windStationName}>
            {isLoading ? <Skeleton width={200} /> : windStation.stationName}
          </Typography>
        </Box>
        {isMobileView && (windStation.provider === 'BOM' || windStation.provider === 'MADIS') && (
          <Box className={styles.attributionInfo}>
            <Information articleId="7MPv5H3kuRp4vSoIdBfeUL" />
          </Box>
        )}
      </Box>

      <Typography
        variant="callout2"
        className={styles.readingDateTime}
        data-testid="wind-station-card-last-observed-reading"
      >
        {isLoading ? (
          <Skeleton width={150} />
        ) : (
          <>
            {isOffline ? 'Last Reading: ' : ''}
            {formatTimestampInTimezone(
              windStation?.lastObserved?.timestamp || 0,
              windStation?.lastObserved?.utcOffset || 0,
              dateFormat,
            )}
          </>
        )}
      </Typography>
      {isOffline && (
        <Typography variant="callout2" className={styles.stationOffline}>
          Station offline
        </Typography>
      )}
      {!isOffline &&
        ((showPaywall && isVerticalView) ||
          (!showPaywall && isVerticalView) ||
          (!showPaywall && !isVerticalView)) && (
          <>
            <Typography
              className={styles.predominantWindReading}
              data-testid="WindStationCardPredominantWindReading"
            >
              {isLoading ? (
                <Skeleton width={125} />
              ) : (
                <WindStationCardPredominantWindReading
                  windSpeedUnit={windSpeedUnit}
                  windSpeed={Number(windStation?.lastObserved?.wind?.speed)}
                  windDirection={Number(windStation?.lastObserved?.wind?.direction)}
                  isOffline={isOffline}
                  noValue={noValue}
                />
              )}
            </Typography>
            <Typography
              variant="callout2"
              className={styles.gustsReading}
              data-testid="WindStationCardGustsReading"
            >
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                <WindStationCardGustsReading
                  windGust={Number(windStation?.lastObserved?.wind?.gust)}
                  windSpeedUnit={windSpeedUnit}
                  isOffline={isOffline}
                  noValue={noValue}
                />
              )}
            </Typography>
          </>
        )}
      {!isOffline && showPaywall && !isVerticalView && (
        <div className={styles.paywallCta} data-testid="MobilePremiumWindStationCTA">
          <MobilePremiumWindStationCTA segmentProperties={segmentProperties} />
        </div>
      )}
    </Card>
  );
};

export default memo(WindStationCard);
